/**
 * Sidebar nav-group expand/collapse state — clean on every load (#803).
 *
 * Behaviour (#803, supersedes the #552 localStorage persistence):
 * - **On load the nav opens clean:** only the FIRST group and the group that
 *   owns the active route are expanded; every other group is collapsed. There
 *   is no cross-session persistence, so expanded groups can never accumulate
 *   into a cluttered "pile of open groups" (the bug Graham reported).
 * - On the dashboard the active group *is* the first group, so a fresh load
 *   shows exactly one group open.
 * - Navigating into a route inside a collapsed group force-expands that group
 *   (the active group always reveals itself).
 * - Manual expand/collapse toggles are honoured for the rest of the session
 *   (in-memory only) and reset to the clean baseline on the next full load.
 *
 * Discoverability exception (#2139): a caller may name additional groups to
 * open in the clean baseline via ``extraExpandedGroupIds`` — used so the
 * read-only Oversight dashboards open by default for a manager
 * (``view_management_dashboards``) who lands on /management and would otherwise
 * never find KPI Reports / Monthly Summary / Deal Pipeline / Leaderboard /
 * Sales Targets behind a collapsed group. This is purely the OPEN-ON-LOAD
 * default for the named role; it does not change any gating, and other roles
 * (no extra ids) keep the unchanged #803 first-group-only behaviour.
 *
 * Keeping it dead-simple — no stored state to drift — matches the
 * non-technical-realtor audience: the sidebar always opens the same tidy way.
 */
import { useCallback, useEffect, useState } from "react";

export interface NavGroupState {
  [groupId: string]: boolean;
}

/**
 * @param groupIds              all group ids that exist in this sidebar, in
 *                              render order (the first id is the "first group"
 *                              opened on load)
 * @param activeGroupId         the group that owns the current route
 *                              (auto-expanded)
 * @param extraExpandedGroupIds additional group ids to open in the clean
 *                              baseline (#2139 — e.g. ["oversight"] for a
 *                              manager). Defaults to none, so the #803
 *                              behaviour is unchanged for every other caller.
 */
export function useNavGroups(
  groupIds: string[],
  activeGroupId: string | null,
  extraExpandedGroupIds: string[] = [],
) {
  const firstGroupId = groupIds[0] ?? null;
  // Stable string keys so the memo deps are statically checkable (the arrays
  // are fresh each render but their CONTENTS drive the baseline).
  const groupIdsKey = JSON.stringify(groupIds);
  const extraExpandedKey = JSON.stringify(extraExpandedGroupIds);

  const buildBaseline = useCallback((): NavGroupState => {
    const base: NavGroupState = {};
    for (const id of groupIds) base[id] = false;
    // The first group is always open so the primary nav is reachable.
    if (firstGroupId && base[firstGroupId] !== undefined) {
      base[firstGroupId] = true;
    }
    // The active route's group is always open (reveals deep-linked groups).
    if (activeGroupId && base[activeGroupId] !== undefined) {
      base[activeGroupId] = true;
    }
    // Caller-named discoverability groups open by default too (#2139).
    for (const id of extraExpandedGroupIds) {
      if (base[id] !== undefined) base[id] = true;
    }
    return base;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupIdsKey, firstGroupId, activeGroupId, extraExpandedKey]);

  const [expanded, setExpanded] = useState<NavGroupState>(buildBaseline);

  // When the active route's group changes, force it open without clobbering the
  // user's in-session choices for other groups (deep-link reveal).
  useEffect(() => {
    if (!activeGroupId) return;
    setExpanded((prev) => {
      if (prev[activeGroupId]) return prev;
      return { ...prev, [activeGroupId]: true };
    });
  }, [activeGroupId]);

  const toggleGroup = useCallback((groupId: string) => {
    setExpanded((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
  }, []);

  const isExpanded = useCallback(
    (groupId: string) => expanded[groupId] ?? false,
    [expanded],
  );

  return { isExpanded, toggleGroup };
}
